import {
  Autocomplete,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { debounce } from '@mui/material/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  AMSLogoColor,
  MdAdd,
  MdCheck,
  MdEdit,
  MdPersonOff,
  MdRefresh,
  MdSettings,
} from '@teamworksdev/icons';
import { getQueryKey } from '@trpc/react-query';
import { DateTime } from 'luxon';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '../../../trpc/react';
import { Page } from '../../components/layout/page';
import { PageHeader } from '../../components/layout/page-header';
import { DataTableWithContext } from '../../components/table/data-table';
import { createColumnHelper } from '../../components/table/data-table-context';
import { useFlags } from '../../context/featureFlag';
import { useToast } from '../../hooks/useToast';
import { ErrorPage } from '../error';
import { LoadingPage } from '../loading';
import { AmsClassicConfigDrawer } from './ams-classic/AmsClassicConfigDrawer';
import { ManualAxleResyncDrawer } from './ManualAxleResyncDrawer';
import { AddEditOrgSheet } from './OrgSheet';
import {
  AmsClassicConfigInput,
  AxleResyncScope,
  CreateOrgInput,
  EditOrgInput,
  Org,
  UserSearchResult,
} from './types';

export const AdminPage = () => {
  const { t } = useTranslation();
  const { grantAdminAccess } = useFlags();
  const orgsQuery = trpc.admin.orgs.useQuery(undefined, {
    staleTime: 10_000,
  });

  const queryClient = useQueryClient();
  const { toast } = useToast();

  const createMutation = trpc.admin.createOrg.useMutation({
    onSuccess: () => {
      orgsQuery.refetch();
      const switchProfilesKey = getQueryKey(
        trpc.user.profilesForSwitch,
        undefined,
        'query',
      );
      queryClient.refetchQueries(switchProfilesKey);
    },
    onError: () => {
      toast({
        title: 'Error creating org',
        severity: 'error',
      });
    },
  });

  const editMutation = trpc.admin.editOrg.useMutation({
    onSuccess: () => {
      orgsQuery.refetch();
      const switchProfilesKey = getQueryKey(
        trpc.user.profilesForSwitch,
        undefined,
        'query',
      );
      queryClient.refetchQueries(switchProfilesKey);
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Error editing organization',
        severity: 'error',
      });
    },
  });

  const onEditMutation = (input: EditOrgInput) => {
    editMutation.mutate(input);
  };

  const onCreateOrg = (input: CreateOrgInput) => {
    createMutation.mutate(input);
  };

  const [reSyncingOrgIds, setReSyncingOrgIds] = useState<string[]>([]);
  const reSyncMutation = trpc.admin.resyncOrgWithAxle.useMutation({
    onSuccess: (_, vars) => {
      setReSyncingOrgIds((ids) => ids.filter((id) => id !== vars.orgId));
      orgsQuery.refetch();
    },
    onError: (_, vars) => {
      setReSyncingOrgIds((ids) => ids.filter((id) => id !== vars.orgId));
      toast({
        title: 'Error re-syncing org',
        severity: 'error',
      });
    },
  });
  const onResyncOrg = (orgId: string, scope: AxleResyncScope) => {
    setReSyncingOrgIds((ids) => [...ids, orgId]);
    reSyncMutation.mutate({
      orgId,
      scope,
    });
    // The resync runs server-side for a while; surface that it started.
    toast({
      title: 'Axle resync initiated',
      severity: 'success',
    });
  };

  const amsClassicConfigMutation =
    trpc.admin.integrations.amsClassic.setConfig.useMutation({
      onSuccess: () => {
        orgsQuery.refetch();
        toast({
          title: 'AMS Classic Integration saved',
          severity: 'success',
        });
      },
      onError: (error) => {
        toast({
          title: 'Error saving AMS Classic Integration',
          description: error.message,
          severity: 'error',
        });
      },
    });

  if (orgsQuery.data) {
    return (
      <Page>
        <PageHeader title={t('admin')} />
        {grantAdminAccess && <AdminAccessSection />}
        <AdminTable
          orgs={orgsQuery.data}
          onCreateOrg={onCreateOrg}
          onEditOrg={onEditMutation}
          onSaveAmsClassicConfig={amsClassicConfigMutation.mutateAsync}
          onResyncOrg={onResyncOrg}
          reSyncingOrgIds={reSyncingOrgIds}
        />
      </Page>
    );
  } else if (orgsQuery.error) {
    return <ErrorPage />;
  } else {
    return <LoadingPage />;
  }
};

const AdminAccessSection = () => {
  const { toast } = useToast();
  const [oktaIdInput, setOktaIdInput] = useState('');
  const [debouncedOktaId, setDebouncedOktaId] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(
    null,
  );

  // Debounce the search input
  const debouncedSetOktaId = useMemo(
    () => debounce((value: string) => setDebouncedOktaId(value), 500),
    [],
  );

  const handleOktaIdChange = useCallback(
    (value: string) => {
      setOktaIdInput(value);
      debouncedSetOktaId(value);
    },
    [debouncedSetOktaId],
  );

  const userSearchQuery = trpc.admin.searchUserByOktaId.useQuery(
    { oktaUserId: debouncedOktaId },
    {
      enabled: debouncedOktaId.length > 0,
    },
  );

  const grantAdminMutation = trpc.admin.grantAdminAccess.useMutation({
    onSuccess: () => {
      toast({
        title: 'Admin access granted',
        severity: 'success',
      });
      setOktaIdInput('');
      setSelectedUser(null);
      userSearchQuery.refetch();
    },
    onError: () => {
      toast({
        title: 'Error granting admin access',
        severity: 'error',
      });
    },
  });

  const handleUserSelect = (user: UserSearchResult | null) => {
    if (!user || user.isAdmin) {
      return;
    }
    setSelectedUser(user);
    grantAdminMutation.mutate({ userId: user.userId });
  };

  const options = userSearchQuery.data ? [userSearchQuery.data] : [];

  return (
    <div className="m-6">
      <Typography variant="h3">Grant Admin Access</Typography>
      <Autocomplete
        options={options}
        value={selectedUser}
        onChange={(_, newValue) => handleUserSelect(newValue)}
        inputValue={oktaIdInput}
        onInputChange={(_, newValue) => handleOktaIdChange(newValue)}
        getOptionLabel={(option) => option.oktaUserId}
        getOptionDisabled={(option) => option.isAdmin}
        isOptionEqualToValue={(option, value) => option.userId === value.userId}
        loading={userSearchQuery.isLoading}
        noOptionsText={
          oktaIdInput.length === 0 ? 'Enter Okta ID to search' : 'No user found'
        }
        renderOption={(props, option) => (
          <li {...props}>
            <div className="flex items-start w-full">
              <div className="flex-grow">
                <div className="font-semibold">{option.oktaUserId}</div>
                <div className="text-sm text-gray-600">
                  {option.profiles.map(
                    (
                      profile: {
                        givenName: string;
                        familyName: string;
                        email: string;
                      },
                      index: number,
                    ) => (
                      <div key={index}>
                        {profile.givenName} {profile.familyName} (
                        {profile.email})
                      </div>
                    ),
                  )}
                </div>
              </div>
              {option.isAdmin && (
                <div className="flex items-center ml-2 text-green-600">
                  <MdCheck size={20} className="mr-1" />
                  <span className="text-sm font-medium whitespace-nowrap">
                    Already Admin
                  </span>
                </div>
              )}
            </div>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search by Okta ID"
            placeholder="Enter exact Okta ID"
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {userSearchQuery.isLoading && debouncedOktaId.length > 0 ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        fullWidth
      />
    </div>
  );
};

const columnHelper = createColumnHelper<Org>();

const AdminTable = ({
  orgs,
  onCreateOrg,
  onEditOrg,
  onResyncOrg,
  onSaveAmsClassicConfig,
  reSyncingOrgIds,
}: {
  orgs: Org[];
  onCreateOrg: (input: CreateOrgInput) => void;
  onEditOrg: (input: EditOrgInput) => void;
  onSaveAmsClassicConfig: (input: AmsClassicConfigInput) => Promise<void>;
  onResyncOrg: (orgId: string, scope: AxleResyncScope) => void;
  reSyncingOrgIds: string[];
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOrgSheetOpen, setIsOrgSheetOpen] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Org>();
  const [orgForAmsConfig, setOrgForAmsConfig] = useState<Org>();
  const [orgForResync, setOrgForResync] = useState<Org>();
  const [searchValue, setSearchValue] = useState('');

  const orgRowClick = useCallback(
    (org: Org) => {
      navigate({
        to: '/admin/orgs/$orgId/settings',
        params: { orgId: org.id },
      });
    },
    [navigate],
  );

  const onEditOrgClick = useCallback((org: Org) => {
    setSelectedOrg(org);
    setIsOrgSheetOpen(true);
  }, []);

  const onAmsClick = useCallback((org: Org) => {
    setOrgForAmsConfig(org);
  }, []);

  // Filter orgs based on search value
  const filteredOrgs = useMemo(() => {
    if (!searchValue) return orgs;
    const lowerSearch = searchValue.toLowerCase();
    return orgs.filter((org) => org.name.toLowerCase().includes(lowerSearch));
  }, [orgs, searchValue]);

  // Define table columns
  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('amsClassicConfig', {
        header: 'Integrations',
        cell: (info) => {
          const org = info.row.original;
          return (
            <div className="flex">
              <AmsIntegrationIcon
                config={org.amsClassicConfig}
                onClick={(e) => {
                  e.stopPropagation();
                  onAmsClick(org);
                }}
              />
            </div>
          );
        },
      }),
      columnHelper.accessor('lastSyncedAt', {
        header: 'Last Synced',
        cell: (info) => {
          const value = info.getValue();
          return value
            ? DateTime.fromISO(value).toLocaleString({
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })
            : 'Never';
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const org = info.row.original;
          const isReSyncing = reSyncingOrgIds.includes(org.id);
          return (
            <div className="flex gap-1">
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onEditOrgClick(org);
                }}
              >
                <MdEdit size={18} />
              </IconButton>
              <IconButton
                disabled={isReSyncing}
                onClick={(e) => {
                  e.stopPropagation();
                  setOrgForResync(org);
                }}
              >
                {isReSyncing ? (
                  <Tooltip title={t('refreshingOrg')}>
                    <CircularProgress size={15} />
                  </Tooltip>
                ) : (
                  <Tooltip title="Manual Axle Resync">
                    <MdRefresh size={18} />
                  </Tooltip>
                )}
              </IconButton>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  orgRowClick(org);
                }}
              >
                <MdSettings size={18} />
              </IconButton>
            </div>
          );
        },
      }),
    ],
    [onAmsClick, onEditOrgClick, orgRowClick, onResyncOrg, reSyncingOrgIds, t],
  );

  return (
    <div className="flex flex-col px-6 pb-6 flex-1 min-h-0">
      <div className="flex flex-1 overflow-hidden">
        <DataTableWithContext<Org>
          title={`${filteredOrgs.length} Orgs`}
          columns={columns}
          data={filteredOrgs}
          enablePagination={true}
          enableSearch={true}
          searchPlaceholder="Search organizations..."
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          initialPageSize={10}
          pageSizeOptions={[10, 25, 50, 100]}
          onRowClick={orgRowClick}
          getRowId={(row) => row.id}
          renderHeaderActions={() => (
            <Button onClick={() => setIsOrgSheetOpen(true)}>
              Create Org
              <MdAdd color="white" className="ml-1" />
            </Button>
          )}
        />
      </div>
      {isOrgSheetOpen && (
        <AddEditOrgSheet
          open
          existingOrgAxleIds={orgs.map((org) => org.axleOrgId)}
          selectedOrg={selectedOrg}
          onClose={() => {
            setIsOrgSheetOpen(false);
            setSelectedOrg(undefined);
          }}
          onEdit={(input) => {
            onEditOrg(input);
            setIsOrgSheetOpen(false);
          }}
          onCreate={(input) => {
            onCreateOrg(input);
            setIsOrgSheetOpen(false);
          }}
        />
      )}
      {orgForAmsConfig && (
        <AmsClassicConfigDrawer
          open={!!orgForAmsConfig}
          onClose={() => setOrgForAmsConfig(undefined)}
          onSave={async (input) => {
            await onSaveAmsClassicConfig(input);
            setOrgForAmsConfig(undefined);
          }}
          org={orgForAmsConfig}
        />
      )}
      {orgForResync && (
        <ManualAxleResyncDrawer
          open
          onClose={() => setOrgForResync(undefined)}
          org={orgForResync}
          onResync={(scope) => {
            const orgId = orgForResync.id;
            setOrgForResync(undefined);
            onResyncOrg(orgId, scope);
          }}
        />
      )}
    </div>
  );
};

function AmsIntegrationIcon({
  config,
  onClick,
}: {
  config: Org['amsClassicConfig'];
  onClick: (e: React.MouseEvent) => void;
}) {
  const { t } = useTranslation();
  let statusIndicator: ReactNode = null;
  let className: string | undefined;
  if (!config) {
    className = 'opacity-30';
  } else if (config.amsClassicSiteUrl) {
    statusIndicator = (
      <div className="absolute rounded-full border-green-200 border-2 bottom-[-2px] right-[-5px] p-[1px] flex">
        <MdCheck
          color="white"
          size={10}
          className="rounded-full bg-green-600 p-[1px]"
        />
      </div>
    );
  } else {
    statusIndicator = (
      <div className="absolute rounded-full border-gray-200 border-2 bottom-[-2px] right-[-5px] p-[1px] flex">
        <MdPersonOff
          size={10}
          color="white"
          className="rounded-full bg-gray-600 p-[1px]"
        />
      </div>
    );
  }

  return (
    <Tooltip title={t('amsClassic')}>
      <div className="relative cursor-pointer" onClick={onClick}>
        <AMSLogoColor className={className} />
        {statusIndicator}
      </div>
    </Tooltip>
  );
}
